import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useQuery } from '@apollo/client';
import dynamic from 'next/dynamic';
import { setError } from '../../../actions';
import Layout from '../../Ad/Layout';
import { getResidenceDataQuery } from '../../../queries';
import AdGalleryPlaceholder from '../../Placeholders/AdGalleryPlaceholder';
import AdLabelPlaceholder from '../../Placeholders/AdLabelPlaceholder';
import getFullPhoneNumbers from '../../../hooks/useFullPhoneNumbers';
import BreadcrumbsPlaceholder from '../../Placeholders/BreadcrumbsPlaceholder';
import { useBridgeState } from '../../../bridge';
import ResidencesNotFoundSection from '../../Ad/ResidencesNotFoundSection';

const Breadcrumbs = dynamic(() => import('../../Breadcrumbs'), {
  loading: BreadcrumbsPlaceholder,
  ssr: false
});

const PhotosGallery = dynamic(() => import('../../Ad/PhotosGallery'), {
  loading: AdGalleryPlaceholder,
  ssr: false
});

const DevelopmentList = dynamic(() => import('../../Ad/DevelopmentList'), {
  ssr: false
});

const InfoSection = dynamic(() => import('../../Ad/Sections/InfoSection'), {
  ssr: false
});

const AdditionalInfoSection = dynamic(
  () => import('../../Ad/Sections/AdditionalInfoSection'),
  {
    ssr: false
  }
);

const LocationSection = dynamic(
  () => import('../../Ad/Sections/LocationSection'),
  {
    ssr: false
  }
);

const ParamsSection = dynamic(() => import('../../Ad/Sections/ParamsSection'), {
  ssr: false
});

const DescriptionSection = dynamic(
  () => import('../../Ad/Sections/DescriptionSection'),
  {
    ssr: false
  }
);

const VideoSection = dynamic(() => import('../../Ad/VideoSection'), {
  ssr: false
});

const InfrastructureSection = dynamic(
  () => import('../../Ad/Sections/InfrastructureSection'),
  {
    ssr: false
  }
);

const ItemsSection = dynamic(() => import('../../Ad/Sections/ItemsSection'), {
  ssr: false
});

const OwnerSection = dynamic(() => import('../../Ad/Sections/OwnerSection'), {
  ssr: false
});

const ApartmentsFilter = dynamic(() => import('../../Ad/ApartmentsFilter'), {
  ssr: false
});

const BottomPhoneButton = dynamic(() => import('../../Ad/BottomPhoneButton'), {
  ssr: false
});
const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: errorState => dispatch(setError(errorState))
  };
};

const ResidencePage = ({ setErrorUI }) => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const router = useRouter();
  const { isWebView } = useBridgeState();
  const residenceSlug = router.query.id;
  const { loading, data, error } = useQuery(
    getResidenceDataQuery(residenceSlug),
    {
      variables: {
        slug: residenceSlug
      },
      ssr: false
    }
  );
  const residenceData = data?.residence || {};
  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    getFullPhoneNumbers(
      residenceSlug,
      residenceData.id,
      client,
      'yasayis-kompleksleri',
      'RESIDENCE',
      null,
      isWebView
    );

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error]);

  if (loading)
    return (
      <>
        <AdLabelPlaceholder />
        <AdGalleryPlaceholder />
      </>
    );

  return (
    <ResidencesNotFoundSection isEmpty={!data?.residence}>
      <Layout
        contentClassNames={'layout__content-residences'}
        header={
          <>
            <Breadcrumbs
              cypress="residences-breadcrumbs"
              additionalClasses="breadcrumbs-residences"
              text="sections.residences.title"
              link="/yasayis-kompleksleri"
              residences={true}
              name={residenceData.companyName}
            />
            <PhotosGallery
              photos={residenceData.photos}
              residence={residenceData}
              {...{ onClickHandler, phonesShown }}
            />
          </>
        }
      >
        <DevelopmentList
          developer={residenceData.companyName}
          imageUrl={residenceData.logoUrl}
          deadline={residenceData.deadline}
        />
        <InfoSection
          priceMax={residenceData.price?.total?.maximum}
          priceMin={residenceData.price?.total?.minimum}
          currency={residenceData.price?.currency}
          deadline={residenceData.deadline}
          apartments={residenceData.apartments}
          hasBillOfSale={residenceData.hasBillOfSale}
        />
        {residenceData.specialOffers?.length > 0 && (
          <AdditionalInfoSection specialOffers={residenceData.specialOffers} />
        )}
        <LocationSection
          city={residenceData.city?.name}
          address={residenceData.address}
          locations={residenceData.locations}
          lat={residenceData.lat}
          lng={residenceData.lng}
        />
        <ParamsSection {...residenceData} />
        <DescriptionSection
          description={residenceData.description}
          isOneDirection={true}
        />

        {(residenceData.youtubeVideoId ||
          residenceData.constructionProgresses?.length > 0) && (
          <VideoSection
            videoId={residenceData.youtubeVideoId}
            width={200}
            height={112}
            construction={residenceData.constructionProgresses}
            name={residenceData.name}
            deadline={residenceData.deadline}
          />
        )}
        {residenceData.infrastructures?.length > 0 && (
          <InfrastructureSection
            infrastructures={residenceData.infrastructures}
          />
        )}
        <OwnerSection
          ownerName={residenceData.name}
          imageUrl={residenceData.logoUrl}
          phonesMasked={residenceData.phonesMasked}
          contactEmail={residenceData.contactEmail}
          website={residenceData.website}
          brochureUrl={residenceData.brochureUrl}
          clickHandler={onClickHandler}
          {...{ phonesShown, residenceSlug }}
        />
        {residenceData.apartments?.length > 0 && (
          <ApartmentsFilter
            title={t('pages.residence.apartments_in', {
              residence_name: residenceData.name
            })}
            apartmentRooms={residenceData.apartmentRooms}
            filterCardList={residenceData.apartments}
          />
        )}
        {residenceData.itemsCount > 0 && (
          <ItemsSection
            residenceId={residenceData.id}
            name={residenceData.name}
            itemsRooms={residenceData.itemRooms}
            itemsCount={residenceData.itemsCount}
          />
        )}
        <BottomPhoneButton
          stat="residences-phones-btn"
          phones={phonesShown}
          clickHandler={onClickHandler}
          {...{ setIsVisible, isVisible }}
        />
      </Layout>
    </ResidencesNotFoundSection>
  );
};

ResidencePage.propTypes = {
  setErrorUI: PropTypes.func
};

export default connect(null, mapDispatchToProps)(ResidencePage);
